import { apiFetch } from './ApiService.js';
import type { IToastService } from './ToastService.js';
import type { IModalService } from './ModalService.js';

/// Håndterer write-actions for admin-tokens (`data-action="create-token"`
/// + `data-action="revoke-token"`).
///
/// Pattern: browser-JS → Admin's `/api/tokens` broker → MailApi `/admin/tokens`
/// → DB. Bearer-tokenet forlader aldrig serveren.
///
/// Klartekst-token returneres ÉN gang ved issue og vises i reveal-modal med
/// copy-button. Serveren har kun SHA-256-hashen — klartekst kan ikke gendannes.

interface IssueTokenPayload {
  name: string;
  expiresInDays: number | null;
}

interface IssueTokenResponse {
  ok: boolean;
  id?: string | null;
  name?: string | null;
  token?: string | null;
  expiresAt?: string | null;
  detail?: string | null;
}

interface BrokerResponse {
  ok: boolean;
  detail?: string | null;
}

export interface ITokenActionsService {
  createFromModal(modalId: string): Promise<void>;
  revoke(id: string, name: string): Promise<void>;
  closeIssuedModalAndReload(): void;
}

export class TokenActionsService implements ITokenActionsService {
  private static readonly CreateModalId = 'modal-create-token';
  private static readonly IssuedModalId = 'modal-token-issued';

  constructor(
    private readonly toast: IToastService,
    private readonly modal: IModalService,
    private readonly doc: Document = document,
  ) { }

  async createFromModal(modalId: string): Promise<void> {
    const modal = this.doc.getElementById(modalId);
    if (!modal) {
      console.warn(`TokenActionsService: modal '${modalId}' findes ikke`);
      return;
    }

    const payload = this.readPayload(modal);
    if (!this.validate(modal, payload)) return;

    try {
      const resp = await apiFetch('/api/tokens', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });

      const body = await this.readIssueBody(resp);
      if (resp.ok && body.ok && body.token) {
        this.modal.close(TokenActionsService.CreateModalId);
        this.showIssuedModal(body);
        return;
      }

      const msg = body.detail ?? `HTTP ${resp.status}`;
      this.toast.show(`Kunne ikke generere token: ${msg}`, 'error');
    } catch (e) {
      console.error(e);
      this.toast.show('Netværksfejl — kunne ikke nå Admin-server', 'error');
    }
  }

  async revoke(id: string, name: string): Promise<void> {
    if (!confirm(`Tilbagekald token "${name}"? Den vil straks holde op med at virke.`)) {
      return;
    }

    try {
      const resp = await apiFetch(`/api/tokens/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
      });

      const body = await this.readBrokerBody(resp);
      if (resp.ok && body.ok) {
        this.toast.show(`Token "${name}" tilbagekaldt`, 'success');
        window.location.reload();
        return;
      }

      const msg = body.detail ?? `HTTP ${resp.status}`;
      this.toast.show(`Kunne ikke tilbagekalde: ${msg}`, 'error');
    } catch (e) {
      console.error(e);
      this.toast.show('Netværksfejl — kunne ikke nå Admin-server', 'error');
    }
  }

  /// Lukker reveal-modal og reloader siden så liste opdateres med ny row.
  closeIssuedModalAndReload(): void {
    this.modal.close(TokenActionsService.IssuedModalId);
    window.location.reload();
  }

  // ---- private helpers ----

  private readPayload(modal: HTMLElement): IssueTokenPayload {
    const nameInput = modal.querySelector<HTMLInputElement>('[name="name"]');
    const expiresInput = modal.querySelector<HTMLInputElement>('[name="expiresInDays"]');
    const raw = (expiresInput?.value ?? '').trim();
    return {
      name: (nameInput?.value ?? '').trim(),
      expiresInDays: raw === '' ? null : Number(raw),
    };
  }

  private validate(modal: HTMLElement, p: IssueTokenPayload): boolean {
    this.clearErrors(modal);
    let ok = true;

    if (!p.name) {
      this.showFieldError(modal, 'name', 'Navn er påkrævet');
      ok = false;
    }

    if (p.expiresInDays !== null) {
      if (!Number.isFinite(p.expiresInDays) || p.expiresInDays < 1 || p.expiresInDays > 3650) {
        this.showFieldError(modal, 'expiresInDays', 'Skal være mellem 1 og 3650 dage');
        ok = false;
      }
    }

    return ok;
  }

  private showIssuedModal(body: IssueTokenResponse): void {
    const modal = this.doc.getElementById(TokenActionsService.IssuedModalId);
    if (!modal || !body.token) return;

    const nameEl = modal.querySelector<HTMLElement>('[data-token-name]');
    if (nameEl) nameEl.textContent = body.name ?? '(unavngivet)';

    const expiresLine = modal.querySelector<HTMLElement>('[data-token-expires-line]');
    if (expiresLine) {
      expiresLine.textContent = body.expiresAt
        ? `Udløber ${new Date(body.expiresAt).toLocaleDateString('da-DK')}.`
        : 'Udløber aldrig.';
    }

    const tokenEl = modal.querySelector<HTMLElement>('[data-token-value]');
    if (tokenEl) tokenEl.textContent = body.token;

    const copyBtn = modal.querySelector<HTMLElement>('[data-action="copy-text"]');
    if (copyBtn) copyBtn.setAttribute('data-text', body.token);

    this.modal.open(TokenActionsService.IssuedModalId);
  }

  private clearErrors(modal: HTMLElement): void {
    modal.querySelectorAll<HTMLElement>('[data-field-error]').forEach(el => {
      el.textContent = '';
    });
  }

  private showFieldError(modal: HTMLElement, field: string, msg: string): void {
    const el = modal.querySelector<HTMLElement>(`[data-field-error="${field}"]`);
    if (el) el.textContent = msg;
  }

  private async readIssueBody(resp: Response): Promise<IssueTokenResponse> {
    try {
      return await resp.json() as IssueTokenResponse;
    } catch {
      return { ok: false, detail: `HTTP ${resp.status}` };
    }
  }

  private async readBrokerBody(resp: Response): Promise<BrokerResponse> {
    try {
      return await resp.json() as BrokerResponse;
    } catch {
      return { ok: false, detail: `HTTP ${resp.status}` };
    }
  }
}
